import { API } from "config";
import type { FileVariant } from "types";

export type PreviewKind = "html" | "image" | "pdf" | "code" | "unsupported";

export interface PreviewInfo {
  kind: PreviewKind;
  language?: string;
  mimeType?: string;
}

export const TEXT_SIZE_LIMIT = 1_000_000;

export function getPreviewKind(fileName: string): PreviewInfo {
  const lower = fileName.toLowerCase();
  if (/\.(html|htm)$/.test(lower))
    return { kind: "html", mimeType: "text/html" };
  if (/\.pdf$/.test(lower)) return { kind: "pdf", mimeType: "application/pdf" };
  if (/\.(png|jpe?g|gif|webp|svg)$/.test(lower)) {
    const ext = lower.split(".").pop() ?? "png";
    const mimeMap: Record<string, string> = {
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
    };
    return { kind: "image", mimeType: mimeMap[ext] ?? "image/png" };
  }
  const codeMap: Record<string, string> = {
    py: "python",
    js: "javascript",
    jsx: "jsx",
    ts: "typescript",
    tsx: "tsx",
    json: "json",
    yaml: "yaml",
    yml: "yaml",
    sh: "bash",
    bash: "bash",
    sql: "sql",
    css: "css",
    scss: "scss",
    md: "markdown",
    xml: "xml",
    toml: "toml",
    ini: "ini",
    csv: "text",
    tsv: "text",
    txt: "text",
    log: "text",
  };
  const ext = lower.split(".").pop() ?? "";
  if (ext in codeMap) return { kind: "code", language: codeMap[ext] };
  return { kind: "unsupported" };
}

export function hasToggleView(kind: PreviewKind, ext: string): boolean {
  if (kind === "html") return true;
  if (kind === "image" && ext === "svg") return true;
  if (kind === "code" && (ext === "md" || ext === "json")) return true;
  return false;
}

/** File API permalink of the first variant of `variantType`, or null if there is none. */
export function findVariantPermalink(
  variants: FileVariant[],
  variantType: string
): string | null {
  const variant = variants.find(
    (candidate) => candidate.variant_type === variantType
  );
  if (!variant) return null;
  return `${API.fileApi.url}/api/v1/variant/${variant.variant_id}/permalink`;
}

/** What `FilePreviewModal` opens for a file. */
export interface FilePreviewTarget {
  /** Permalink of the variant the modal renders. */
  permalink: string;
  /** Set when the rendered variant's type differs from the file name's. */
  previewKindOverride?: PreviewKind;
  /** Set when the modal's Download button should save a different variant than it renders. */
  downloadPermalink?: string;
}

/**
 * Picks the variant to preview. A file the browser can render previews its
 * `original` variant. Any other file previews its `pdf_preview` variant
 * (PowerPoint, ADR 055) and downloads its `original`. Returns null when the
 * needed variant is missing, meaning the file has no preview.
 */
export function getFilePreviewTarget(
  fileName: string,
  variants: FileVariant[]
): FilePreviewTarget | null {
  const originalPermalink = findVariantPermalink(variants, "original");
  if (getPreviewKind(fileName).kind !== "unsupported") {
    if (originalPermalink === null) return null;
    return { permalink: originalPermalink };
  }
  const pdfPreviewPermalink = findVariantPermalink(variants, "pdf_preview");
  if (pdfPreviewPermalink === null) return null;
  return {
    permalink: pdfPreviewPermalink,
    previewKindOverride: "pdf",
    downloadPermalink: originalPermalink ?? undefined,
  };
}
